// Lightweight Google Analytics 4 helper.
//
// Loading + production-gating happens in public/index.html — by the time
// any function here runs, either:
//   * window.gtag is a real function (production on furmais.com) → events flow
//   * window.gtag is the bootstrap stub that just pushes to dataLayer
//     without ever loading gtag.js (preview / dev) → silent no-op
//
// We do NOT install react-ga4 / react-gtag — gtag.js is loaded natively.

const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "";

/** Returns true only when we're actually expected to send GA events. */
export function gaEnabled() {
    if (!MEASUREMENT_ID) return false;
    if (typeof window === "undefined") return false;
    if (!window.__GA_LOADED__) return false; // host-gate set this in index.html
    return typeof window.gtag === "function";
}

/**
 * Sanitize a SPA path before sending to GA. Strips opaque tokens from
 * share / review / collaborator-invite style paths so we never send
 * personally identifying or sensitive values to Google.
 *
 * Examples:
 *   /review/abc123                          → /review/[token]
 *   /share/91977d19/abc123                  → /share/[projectId]/[token]
 *   /collab/invite/abc123                   → /collab/invite/[token]
 *   /dashboard                              → /dashboard          (unchanged)
 *   /clients/cl-1/projects/pr-2/edit        → /clients/cl-1/projects/pr-2/edit (unchanged)
 *   /clients/cl-1?tab=overview&token=xyz    → /clients/cl-1?tab=overview&token=[token]
 */
export function sanitizePath(pathWithSearch) {
    if (!pathWithSearch || typeof pathWithSearch !== "string") return "/";
    // Split path vs. search so we sanitize each side separately.
    const qIdx = pathWithSearch.indexOf("?");
    let pathname = qIdx >= 0 ? pathWithSearch.slice(0, qIdx) : pathWithSearch;
    let search = qIdx >= 0 ? pathWithSearch.slice(qIdx) : "";

    // Path-level token-strip rules (token always last segment, opaque).
    pathname = pathname
        .replace(/^\/review\/[^/]+/i, "/review/[token]")
        .replace(/^\/share\/([^/]+)\/[^/]+/i, "/share/[projectId]/[token]")
        .replace(/(\/(?:collab|invite|reset)\/[^/]*?\/)[^/]+/gi, "$1[token]");

    // Search-level token-strip — common ?token=... / ?invite=... / ?key=...
    if (search) {
        try {
            const params = new URLSearchParams(search.slice(1));
            const sensitive = ["token", "invite", "key", "session", "auth", "code"];
            sensitive.forEach((k) => {
                if (params.has(k)) params.set(k, "[token]");
            });
            const next = params.toString();
            search = next ? "?" + next : "";
        } catch {
            // If URLSearchParams ever fails, just drop search entirely
            // rather than risk leaking.
            search = "";
        }
    }

    return pathname + search;
}

/** Fire a manual SPA page_view event with the sanitised path. */
export function trackPageview(pathWithSearch, title) {
    if (!gaEnabled()) return;
    const safePath = sanitizePath(pathWithSearch);
    const origin =
        typeof window !== "undefined" && window.location
            ? window.location.origin
            : "";
    window.gtag("event", "page_view", {
        page_path: safePath,
        page_location: origin + safePath,
        page_title: title || (typeof document !== "undefined" ? document.title : ""),
        send_to: MEASUREMENT_ID,
    });
}
